import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import AdminLayout from '../../components/AdminLayout';
import {
  FiMessageCircle, FiAlertTriangle as FiBug, FiStar, FiRefreshCw, FiFilter, FiSearch,
  FiX, FiSend, FiEye, FiClock, FiCheckCircle, FiXCircle, FiPlay,
  FiUser, FiCalendar, FiMessageSquare, FiEdit, FiSave, FiAlertTriangle
} from 'react-icons/fi';

// Import shared admin components
import {
  AdminContainer, AdminHeader, HeaderContent, HeaderText, HeaderActions,
  ActionButton, MainContent, StatsGrid, StatCard, StatHeader, StatIcon,
  StatValue, StatLabel, SearchAndFilters, SearchBox, FilterSelect,
  LoadingSpinner, ErrorMessage
} from '../../styles/AdminComponents';

// Feedback-specific styled components
import styled from 'styled-components';

const FeedbackLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  height: calc(100vh - 300px);
  min-height: 600px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    height: auto;
    min-height: auto;
  }
`;

const FeedbackListPanel = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FeedbackDetailPanel = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  
  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .header-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5rem;
    
    .count {
      font-size: 0.9rem;
      color: #6b7280;
      font-weight: 500;
    }
  }
`;

const FilterBar = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  
  .filter-groups {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    
    @media (min-width: 768px) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }
  
  .filter-group {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    
    .filter-label {
      font-size: 0.875rem;
      font-weight: 500;
      color: #374151;
      display: flex;
      align-items: center;
      min-width: fit-content;
    }
  }
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.$active ? '#3b82f6' : '#d1d5db'};
  border-radius: 8px;
  background: ${props => props.$active ? '#3b82f6' : 'white'};
  color: ${props => props.$active ? 'white' : '#374151'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  
  &:hover {
    background: ${props => props.$active ? '#2563eb' : '#f9fafb'};
    border-color: ${props => props.$active ? '#2563eb' : '#9ca3af'};
  }
  
  &:focus {
    outline: none;
    ring: 2px;
    ring-color: rgba(59, 130, 246, 0.2);
  }
`;

const FeedbackList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const FeedbackCard = styled.div`
  background: ${props => props.$selected ? '#eff6ff' : 'white'};
  border: 1px solid ${props => props.$selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .feedback-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    
    .feedback-icon {
      font-size: 1.5rem;
      flex-shrink: 0;
    }
    
    .feedback-title {
      font-weight: 600;
      color: #111827;
      font-size: 1rem;
      line-height: 1.4;
      flex: 1;
      margin: 0;
    }
  }
  
  .feedback-description {
    color: #6b7280;
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 0.75rem;
  }
  
  .feedback-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
  
  .feedback-timestamp {
    font-size: 0.8rem;
    color: #9ca3af;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: capitalize;
  
  ${props => {
    switch (props.$variant) {
      case 'bug':
        return 'background: #fee2e2; color: #991b1b;';
      case 'feature_request':
        return 'background: #e0f2fe; color: #075985;';
      case 'feedback':
        return 'background: #f3e8ff; color: #6b21a8;';
      case 'open':
        return 'background: #fef3c7; color: #92400e;';
      case 'in_progress':
        return 'background: #dbeafe; color: #1e40af;';
      case 'resolved':
        return 'background: #dcfce7; color: #166534;';
      case 'closed':
        return 'background: #f3f4f6; color: #374151;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const FeedbackDetail = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  .detail-content {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f5f9;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;
    }
  }
  
  .detail-actions {
    padding: 1.5rem;
    border-top: 1px solid #e5e7eb;
    background: #f8fafc;
  }
  
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #9ca3af;
    text-align: center;
    padding: 2rem;
    
    .empty-icon {
      font-size: 4rem;
      margin-bottom: 1rem;
      opacity: 0.5;
    }
    
    .empty-title {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }
    
    .empty-description {
      font-size: 0.9rem;
      line-height: 1.5;
    }
  }
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    
    h4 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: #111827;
    }
  }
  
  .section-content {
    .description-text {
      color: #374151;
      line-height: 1.6;
      white-space: pre-wrap;
      background: #f8fafc;
      padding: 1rem;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }
    
    .user-info {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      background: #f3f4f6;
      padding: 0.5rem 1rem;
      border-radius: 8px;
      font-family: 'Monaco', 'Consolas', monospace;
      font-size: 0.9rem;
      color: #374151;
    }
    
    .contact-info {
      color: #3b82f6;
      font-weight: 500;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
    font-size: 0.9rem;
  }
  
  select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 120px;
    font-family: inherit;
    line-height: 1.5;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const FeedbackPage = () => {
  const [feedback, setFeedback] = useState([]);
  const [filteredFeedback, setFilteredFeedback] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminReply, setAdminReply] = useState('');
  const [status, setStatus] = useState('open');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedbackStats, setFeedbackStats] = useState(null);
  
  const adminRank = localStorage.getItem('adminRank');

  useEffect(() => {
    if (adminRank !== 'owner' && adminRank !== 'manager') return;
    loadFeedback();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const filtered = feedback.filter(item => {
      const statusMatch = feedbackFilter === 'all' || item.status === feedbackFilter;
      const typeMatch = feedbackTypeFilter === 'all' || item.type === feedbackTypeFilter;
      const searchMatch = !searchTerm || 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.user_id && item.user_id.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return statusMatch && typeMatch && searchMatch;
    });
    
    setFilteredFeedback(filtered);
  }, [feedback, feedbackFilter, feedbackTypeFilter, searchTerm]);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const res = await axios.get('/api/admin/feedback', { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      if (res.data.success) {
        const feedbackData = res.data.feedback || [];
        setFeedback(feedbackData);
        
        // Calculate comprehensive feedback stats
        const stats = {
          total_feedback: feedbackData.length,
          open_feedback: feedbackData.filter(f => f.status === 'open').length,
          in_progress: feedbackData.filter(f => f.status === 'in_progress').length,
          resolved_feedback: feedbackData.filter(f => f.status === 'resolved').length,
          bugs: feedbackData.filter(f => f.type === 'bug').length,
          feature_requests: feedbackData.filter(f => f.type === 'feature_request').length,
          general_feedback: feedbackData.filter(f => f.type === 'feedback').length,
          recent_feedback: feedbackData.filter(f => {
            const dayAgo = new Date();
            dayAgo.setDate(dayAgo.getDate() - 7);
            return new Date(f.created_at) > dayAgo;
          }).length
        };
        setFeedbackStats(stats);
      }
    } catch (err) {
      console.error('Error loading feedback:', err);
      setError('Failed to load feedback');
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const refreshFeedback = () => {
    loadFeedback();
  };

  const handleStatusUpdate = async () => {
    if (!selectedFeedback) return;
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `/api/admin/feedback/${selectedFeedback.id}`,
        {
          status,
          response: adminReply || null,
          notify: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success('Feedback updated successfully!');
        setSelectedFeedback(null);
        setAdminReply('');
        setStatus('open');
        loadFeedback();
      } else {
        toast.error(res.data.message || 'Failed to update feedback');
      }
    } catch (err) {
      console.error('Error updating feedback:', err);
      toast.error('Failed to update feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeedbackSelect = (item) => {
    setSelectedFeedback(item);
    setAdminReply(item.response || '');
    setStatus(item.status);
  };

  const clearSelection = () => {
    setSelectedFeedback(null);
    setAdminReply('');
    setStatus('open');
  };

  const getFeedbackIcon = (type) => {
    switch (type) {
      case 'bug':
        return <FiBug style={{ color: '#ef4444' }} />;
      case 'feature_request':
        return <FiStar style={{ color: '#3b82f6' }} />;
      default:
        return <FiMessageCircle style={{ color: '#8b5cf6' }} />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <FiAlertTriangle />;
      case 'in_progress':
        return <FiPlay />;
      case 'resolved':
        return <FiCheckCircle />;
      case 'closed':
        return <FiXCircle />;
      default:
        return <FiClock />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Access control check
  if (adminRank !== 'owner' && adminRank !== 'manager') {
    return (
      <AdminLayout>
        <AdminContainer>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '50vh',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <FiXCircle size={48} style={{ marginBottom: '1rem', color: '#ef4444' }} />
            <h2>Access Restricted</h2>
            <p>You need to be a manager or owner to access the feedback management section.</p>
          </div>
        </AdminContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminContainer>
        <AdminHeader>
          <HeaderContent>
            <HeaderText>
              <h1>Feedback Management</h1>
              <p>Review and respond to user feedback, bug reports, and feature requests</p>
            </HeaderText>
            <HeaderActions>
              <ActionButton onClick={refreshFeedback} variant="secondary">
                <FiRefreshCw />
                <span className="btn-text">Refresh</span>
              </ActionButton>
            </HeaderActions>
          </HeaderContent>
        </AdminHeader>

        <MainContent>
          {/* Enhanced Feedback Stats Section */}
          {feedbackStats && (
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiMessageCircle color="#3b82f6" /></StatIcon>
                  <h3>Total Feedback</h3>
                </StatHeader>
                <StatValue>{feedbackStats.total_feedback}</StatValue>
                <StatLabel>All Submissions</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiAlertTriangle color="#f59e0b" /></StatIcon>
                  <h3>Open</h3>
                </StatHeader>
                <StatValue>{feedbackStats.open_feedback}</StatValue>
                <StatLabel>Needs Attention</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiPlay color="#3b82f6" /></StatIcon>
                  <h3>In Progress</h3>
                </StatHeader>
                <StatValue>{feedbackStats.in_progress}</StatValue>
                <StatLabel>Being Worked On</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCheckCircle color="#10b981" /></StatIcon>
                  <h3>Resolved</h3>
                </StatHeader>
                <StatValue>{feedbackStats.resolved_feedback}</StatValue>
                <StatLabel>Completed</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiBug color="#ef4444" /></StatIcon>
                  <h3>Bug Reports</h3>
                </StatHeader>
                <StatValue>{feedbackStats.bugs}</StatValue>
                <StatLabel>Issues Reported</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiStar color="#8b5cf6" /></StatIcon>
                  <h3>Feature Requests</h3>
                </StatHeader>
                <StatValue>{feedbackStats.feature_requests}</StatValue>
                <StatLabel>New Ideas</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiMessageSquare color="#06b6d4" /></StatIcon>
                  <h3>General Feedback</h3>
                </StatHeader>
                <StatValue>{feedbackStats.general_feedback}</StatValue>
                <StatLabel>User Comments</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCalendar color="#10b981" /></StatIcon>
                  <h3>Recent</h3>
                </StatHeader>
                <StatValue>{feedbackStats.recent_feedback}</StatValue>
                <StatLabel>Last 7 Days</StatLabel>
              </StatCard>
            </StatsGrid>
          )}

          <SearchAndFilters>
            <SearchBox>
              <FiSearch />
              <input
                type="text"
                placeholder="Search feedback by title, description, or user ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
          </SearchAndFilters>

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <FeedbackLayout>
              {/* Feedback List Panel */}
              <FeedbackListPanel>
                <PanelHeader>
                  <div className="header-meta">
                    <h3>
                      <FiMessageCircle />
                      Feedback List
                    </h3>
                    <span className="count">{filteredFeedback.length} items</span>
                  </div>
                </PanelHeader>

                <FilterBar>
                  <div className="filter-groups">
                    <div className="filter-group">
                      <span className="filter-label">
                        <FiFilter />
                        Status:
                      </span>
                      <FilterButton 
                        $active={feedbackFilter === 'all'} 
                        onClick={() => setFeedbackFilter('all')}
                      >
                        All
                      </FilterButton>
                      <FilterButton 
                        $active={feedbackFilter === 'open'} 
                        onClick={() => setFeedbackFilter('open')}
                      >
                        <FiAlertTriangle />
                        Open
                      </FilterButton>
                      <FilterButton 
                        $active={feedbackFilter === 'in_progress'} 
                        onClick={() => setFeedbackFilter('in_progress')}
                      >
                        <FiPlay />
                        In Progress
                      </FilterButton>
                      <FilterButton 
                        $active={feedbackFilter === 'resolved'} 
                        onClick={() => setFeedbackFilter('resolved')}
                      >
                        <FiCheckCircle />
                        Resolved
                      </FilterButton>
                      <FilterButton 
                        $active={feedbackFilter === 'closed'} 
                        onClick={() => setFeedbackFilter('closed')}
                      >
                        <FiXCircle />
                        Closed
                      </FilterButton>
                    </div>

                    <div className="filter-group">
                      <span className="filter-label">Type:</span>
                      <FilterButton 
                        $active={feedbackTypeFilter === 'all'} 
                        onClick={() => setFeedbackTypeFilter('all')}
                      >
                        All Types
                      </FilterButton>
                      <FilterButton 
                        $active={feedbackTypeFilter === 'bug'} 
                        onClick={() => setFeedbackTypeFilter('bug')}
                      >
                        <FiBug />
                        Bugs
                      </FilterButton>
                      <FilterButton 
                        $active={feedbackTypeFilter === 'feature_request'} 
                        onClick={() => setFeedbackTypeFilter('feature_request')}
                      >
                        <FiStar />
                        Features
                      </FilterButton>
                      <FilterButton 
                        $active={feedbackTypeFilter === 'feedback'} 
                        onClick={() => setFeedbackTypeFilter('feedback')}
                      >
                        <FiMessageCircle />
                        Feedback
                      </FilterButton>
                    </div>
                  </div>
                </FilterBar>

                <FeedbackList>
                  {filteredFeedback.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '3rem 1rem', 
                      color: '#9ca3af' 
                    }}>
                      <FiMessageCircle size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>
                        {searchTerm || feedbackFilter !== 'all' || feedbackTypeFilter !== 'all' 
                          ? 'No feedback matches your filters' 
                          : 'No feedback found'}
                      </p>
                    </div>
                  ) : (
                    filteredFeedback.map(item => (
                      <FeedbackCard
                        key={item.id}
                        $selected={selectedFeedback?.id === item.id}
                        onClick={() => handleFeedbackSelect(item)}
                      >
                        <div className="feedback-header">
                          <div className="feedback-icon">
                            {getFeedbackIcon(item.type)}
                          </div>
                          <h4 className="feedback-title">{item.title}</h4>
                        </div>
                        
                        <div className="feedback-description">
                          {item.description.length > 100 
                            ? item.description.slice(0, 100) + '...' 
                            : item.description}
                        </div>
                        
                        <div className="feedback-meta">
                          <StatusBadge $variant={item.type}>
                            {getFeedbackIcon(item.type)}
                            {item.type.replace('_', ' ')}
                          </StatusBadge>
                          <StatusBadge $variant={item.status}>
                            {getStatusIcon(item.status)}
                            {item.status.replace('_', ' ')}
                          </StatusBadge>
                          {item.platform && (
                            <StatusBadge $variant="default">{item.platform}</StatusBadge>
                          )}
                          {item.version && (
                            <StatusBadge $variant="default">v{item.version}</StatusBadge>
                          )}
                        </div>
                        
                        <div className="feedback-timestamp">
                          <FiCalendar />
                          {formatDate(item.created_at)}
                        </div>
                      </FeedbackCard>
                    ))
                  )}
                </FeedbackList>
              </FeedbackListPanel>

              {/* Feedback Detail Panel */}
              <FeedbackDetailPanel>
                <PanelHeader>
                  <div className="header-meta">
                    <h3>
                      <FiEye />
                      Feedback Details
                    </h3>
                    {selectedFeedback && (
                      <ActionButton onClick={clearSelection} variant="secondary" size="small">
                        <FiX />
                        Clear
                      </ActionButton>
                    )}
                  </div>
                </PanelHeader>

                <FeedbackDetail>
                  {selectedFeedback ? (
                    <>
                      <div className="detail-content">
                        <DetailSection>
                          <div className="section-header">
                            <h4>{selectedFeedback.title}</h4>
                          </div>
                          <div className="section-content">
                            <div className="feedback-meta" style={{ marginBottom: '1rem' }}>
                              <StatusBadge $variant={selectedFeedback.type}>
                                {getFeedbackIcon(selectedFeedback.type)}
                                {selectedFeedback.type.replace('_', ' ')}
                              </StatusBadge>
                              <StatusBadge $variant={selectedFeedback.status}>
                                {getStatusIcon(selectedFeedback.status)}
                                {selectedFeedback.status.replace('_', ' ')}
                              </StatusBadge>
                              {selectedFeedback.platform && (
                                <StatusBadge $variant="default">{selectedFeedback.platform}</StatusBadge>
                              )}
                              {selectedFeedback.version && (
                                <StatusBadge $variant="default">v{selectedFeedback.version}</StatusBadge>
                              )}
                            </div>
                          </div>
                        </DetailSection>

                        {selectedFeedback.user_id && (
                          <DetailSection>
                            <div className="section-header">
                              <FiUser />
                              <h4>User Information</h4>
                            </div>
                            <div className="section-content">
                              <div className="user-info">
                                <FiUser />
                                {selectedFeedback.user_id}
                              </div>
                            </div>
                          </DetailSection>
                        )}

                        <DetailSection>
                          <div className="section-header">
                            <FiMessageSquare />
                            <h4>Description</h4>
                          </div>
                          <div className="section-content">
                            <div className="description-text">
                              {selectedFeedback.description}
                            </div>
                          </div>
                        </DetailSection>

                        {selectedFeedback.telegram && (
                          <DetailSection>
                            <div className="section-header">
                              <FiUser />
                              <h4>Contact Information</h4>
                            </div>
                            <div className="section-content">
                              <div className="contact-info">
                                Telegram: {selectedFeedback.telegram}
                              </div>
                            </div>
                          </DetailSection>
                        )}
                      </div>

                      <div className="detail-actions">
                        <FormGroup>
                          <label>
                            <FiEdit />
                            Status
                          </label>
                          <select 
                            value={status} 
                            onChange={e => setStatus(e.target.value)}
                          >
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                        </FormGroup>

                        <FormGroup>
                          <label>
                            <FiMessageSquare />
                            Response
                          </label>
                          <textarea 
                            value={adminReply} 
                            onChange={e => setAdminReply(e.target.value)}
                            placeholder="Enter your response to the user..."
                          />
                        </FormGroup>

                        <ActionButtons>
                          <ActionButton onClick={clearSelection} variant="secondary">
                            Cancel
                          </ActionButton>
                          <ActionButton 
                            onClick={handleStatusUpdate} 
                            disabled={isSubmitting}
                          >
                            <FiSave />
                            {isSubmitting ? 'Updating...' : 'Update Feedback'}
                          </ActionButton>
                        </ActionButtons>
                      </div>
                    </>
                  ) : (
                    <div className="empty-state">
                      <FiMessageCircle className="empty-icon" />
                      <div className="empty-title">Select Feedback</div>
                      <div className="empty-description">
                        Choose a feedback item from the list to view details and respond
                      </div>
                    </div>
                  )}
                </FeedbackDetail>
              </FeedbackDetailPanel>
            </FeedbackLayout>
          )}
        </MainContent>
      </AdminContainer>
    </AdminLayout>
  );
};

export default FeedbackPage; 